import { useMemo } from 'react';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getRegistrationKind } from './registries/components/recommendation-helpers';
import { hasVisitReportForField } from './registry-visit-reports';
import { isFungicideOrInsecticideTypeName, isHerbicideTypeName } from './registry-product-types';
import { useCampaignRegistries } from './use-campaign-registries';

export { hasVisitReportForField };

/** Fungicide/insecticide applications or disease/plague recommendations in the campaign window. */
export const useProtectionActivity = ({
    fieldId,
    cropMonitoring,
    enabled,
}: {
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    enabled: boolean;
}): { hasActivity: boolean; isLoading: boolean } => {
    const { fetchEnabled, dateFetchEnabled, applicationsQuery, cropProtectionRecommendationsQuery } =
        useCampaignRegistries({ fieldId, cropMonitoring, enabled });
    const { typeNameById } = useCropProtectionProducts();

    const applications = applicationsQuery.data?.results ?? [];
    const cropProtectionRecommendations = cropProtectionRecommendationsQuery.data?.results?.data ?? [];

    const hasActivity = useMemo(
        () =>
            applications.some((register) => isFungicideOrInsecticideTypeName(typeNameById(register.type))) ||
            cropProtectionRecommendations.some(
                (recommendation) =>
                    Boolean(recommendation.diseaseRecommendation) || Boolean(recommendation.plagueRecommendation)
            ),
        [applications, cropProtectionRecommendations, typeNameById]
    );

    const isLoading =
        (fetchEnabled && applicationsQuery.isLoading) ||
        (dateFetchEnabled && cropProtectionRecommendationsQuery.isLoading);

    return { hasActivity, isLoading };
};

/** Herbicide application or weed recommendation in the campaign window. */
export const useHerbicideActivity = ({
    fieldId,
    cropMonitoring,
    enabled,
}: {
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    enabled: boolean;
}): { hasHerbicideApplication: boolean; hasHerbicideRecommendation: boolean; isLoading: boolean } => {
    const { fetchEnabled, dateFetchEnabled, applicationsQuery, cropProtectionRecommendationsQuery } =
        useCampaignRegistries({ fieldId, cropMonitoring, enabled });
    const { typeNameById } = useCropProtectionProducts();

    const applications = applicationsQuery.data?.results ?? [];
    const cropProtectionRecommendations = cropProtectionRecommendationsQuery.data?.results?.data ?? [];

    const hasHerbicideApplication = useMemo(
        () => applications.some((register) => isHerbicideTypeName(typeNameById(register.type))),
        [applications, typeNameById]
    );
    const hasHerbicideRecommendation = useMemo(
        () => cropProtectionRecommendations.some((recommendation) => Boolean(recommendation.weedV2)),
        [cropProtectionRecommendations]
    );

    const isLoading =
        (fetchEnabled && applicationsQuery.isLoading) ||
        (dateFetchEnabled && cropProtectionRecommendationsQuery.isLoading);

    return { hasHerbicideApplication, hasHerbicideRecommendation, isLoading };
};

/** Any barbecho-relevant activity: herbicide, weed recommendation, or hybrid recommendation. */
export const useBarbechoActivity = ({
    fieldId,
    cropMonitoring,
    enabled,
}: {
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    enabled: boolean;
}): { hasActivity: boolean; isLoading: boolean } => {
    const { fetchEnabled, dateFetchEnabled, applicationsQuery, seedRecommendationsQuery, cropProtectionRecommendationsQuery } =
        useCampaignRegistries({ fieldId, cropMonitoring, enabled });
    const { typeNameById } = useCropProtectionProducts();

    const applications = applicationsQuery.data?.results ?? [];
    const seedRecommendations = seedRecommendationsQuery.data?.results?.data ?? [];
    const cropProtectionRecommendations = cropProtectionRecommendationsQuery.data?.results?.data ?? [];

    const hasHerbicideApplication = useMemo(
        () => applications.some((register) => isHerbicideTypeName(typeNameById(register.type))),
        [applications, typeNameById]
    );
    const hasHerbicideRecommendation = useMemo(
        () => cropProtectionRecommendations.some((recommendation) => Boolean(recommendation.weedV2)),
        [cropProtectionRecommendations]
    );
    const hasHybridRecommendation = useMemo(
        () => seedRecommendations.some((recommendation) => getRegistrationKind(recommendation) === 'corn'),
        [seedRecommendations]
    );

    const hasActivity = hasHerbicideApplication || hasHerbicideRecommendation || hasHybridRecommendation;

    const isLoading =
        (fetchEnabled && applicationsQuery.isLoading) ||
        (dateFetchEnabled && seedRecommendationsQuery.isLoading) ||
        (dateFetchEnabled && cropProtectionRecommendationsQuery.isLoading);

    return { hasActivity, isLoading };
};
